import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { GET, httpOk } from "helpers/api"
import { formatDate, formatMoney } from 'helpers/helpers'

import ThermalPrint, { ThermalPrintButton, useThermalPrint } from 'pages/parts/ThermalPrint'

function Preview() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const [data, setData] = useState({})
	const { printRef, handlePrint } = useThermalPrint()
	const [expandedItems, setExpandedItems] = useState({});

	function TreeTable({ data, level = 0 }) {
		return (
			<>
				{data?.map((item, index) => (
					<React.Fragment key={item.id}>
						<tr>
							<td className="text-center">
								{!item.product_parent_id &&
									<span>{index + 1}</span>
								}
							</td>
							<td style={{ 'paddingLeft': `${level * 10 + 10}px` }}
								onClick={(e) => {
									e.stopPropagation(); // Остановить всплытие события клика
									toggleExpand(item.id); // Переключить состояние "открыт/закрыт"
								}}>
								{!item.product_parent_id &&
									<span className={item?.children?.length ? `product-parent` : ''}>{item.product_name}</span>
								}
								{item.product_parent_id &&
									<i className={item?.children?.length ? `product-parent` : ''}>{item.product_name}</i>
								}
							</td>
							<td>{item.product_type_name}</td>
							<td></td>
							<td className="text-end">{item.quantity}</td>
							<td className="text-start">{item.unit_name}</td>
							<td className="text-end">{formatMoney(item.cost_price)}</td>
							<td className="text-end">{formatMoney(item.total_price)}</td>
						</tr>
						{/* Рекурсивный вызов для children */}
						{expandedItems[item.id] && item.children && item.children.length > 0 && (
							<TreeTable
								data={item.children}
								level={level + 1}
							/>
						)}
					</React.Fragment>
				))}
			</>
		);
	}

	const toggleExpand = (id) => {
		setExpandedItems((prev) => ({
			...prev,
			[id]: !prev[id], // Переключаем состояние для текущего элемента
		}));
	};

	async function getData() {
		const response = await GET(`/wms-transfer-branch/${id}`, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('transfer')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('preview')}</h6>
					</div>
				</div>

				<div className="vertical-center">
					<ThermalPrintButton onClick={handlePrint} />
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<h4 className="fw-600">{t('document_from')} {formatDate(data.created_at, 'dd.MM.yy HH:mm')}</h4>
					<div className="flex-column preview-info">
						<div className="d-flex gap-2">
							<div className="fw-600">{t('pos')}:</div>
							<div>{data.pos_name}</div>
						</div>
						{data.wms_name &&
							<div className="d-flex gap-2">
								<div className="fw-600">{t('from_wms')}:</div>
								<div>{data.wms_name}</div>
							</div>
						}
						<div className="d-flex gap-2">
							<div className="fw-600">{t('external_pos')}:</div>
							<div>{data.to_pos_name}</div>
						</div>
						<div className="d-flex gap-2">
							<div className="fw-600">{t('note')}:</div>
							<div>{data.note}</div>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '30%' }}>{t('name2')}</th>
									<th style={{ 'width': '10%' }}>{t('type')}</th>
									<th style={{ 'width': '20%' }}></th>
									<th style={{ 'width': '10%' }} className="text-end">{t('quantity')}</th>
									<th style={{ 'width': '10%' }} className="text-start">{t('unit')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('cost_price')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								<TreeTable data={data.items} />
							</tbody>
							<tfoot>
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
								</tr>
							</tfoot>
						</table>
					</div>

					<div className="d-flex justify-content-end mt-5">
						<button type="button" className="btn btn-warning btn-rounded btn-wide"
							onClick={() => navigate(-1)}>
							{t('cancel')}
						</button>
					</div>
				</div>
			</div>

			<ThermalPrint
				printRef={printRef}
				title={`${t('document_from')} ${formatDate(data.created_at, 'dd.MM.yy HH:mm')}`}
				info={[
					{ label: t('pos'), value: data.pos_name },
					{ label: t('external_pos'), value: data.to_pos_name },
					{ label: t('note'), value: data.note },
				]}
				columns={[
					{ label: t('name2'), width: '42%', numbered: true, render: (item) => item.product_name },
					{ label: t('quantity'), width: '16%', align: 'end', render: (item) => formatMoney(item.quantity, 2) },
					{ label: t('cost_price'), width: '20%', align: 'end', render: (item) => formatMoney(item.cost_price) },
					{ label: t('amount'), width: '22%', align: 'end', render: (item) => formatMoney(item.total_price) },
				]}
				rows={data?.items || []}
			/>
		</>
	)
}

export default Preview